/**
 * Summary statistics over a stream of numbers, for any quantity: intensities,
 * distances, doses. Unit conversion and labelling belong to the display
 * boundary, never here.
 */
export interface SummaryStatistics {
    readonly min: number;
    readonly max: number;
    readonly mean: number;
    /** Population standard deviation (÷N): the samples are the whole population. */
    readonly std: number;
    /** Number of finite samples included. */
    readonly count: number;
}

/**
 * Single-pass Welford: numerically stable at large offsets such as CT's HU
 * range, O(1) memory. Non-finite samples are skipped.
 *
 * @returns null when no finite sample was seen, so the caller surfaces "no
 *   data" rather than a fabricated zero.
 */
export function summaryStatistics(
    samples: Iterable<number>
): SummaryStatistics | null {
    let count = 0;
    let mean = 0;
    // Running sum of squared deviations from the current mean (Welford M2).
    let m2 = 0;
    let min = Infinity;
    let max = -Infinity;
    for (const value of samples) {
        if (!Number.isFinite(value)) {
            continue;
        }
        count += 1;
        const delta = value - mean;
        mean += delta / count;
        m2 += delta * (value - mean);
        if (value < min) {
            min = value;
        }
        if (value > max) {
            max = value;
        }
    }
    if (count === 0) {
        return null;
    }
    return { min, max, mean, std: Math.sqrt(m2 / count), count };
}
